import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { ClearLocationUsersState, SendExistingInvitedUserEmail, SendNewInvitedUserEmail } from "../actions/customer-location.actions";

import { Injectable } from "@angular/core";
import { CustomerPortalDbService } from "@app/customer-portal/services/customer-portal-db.service";
import { CustomerPortalService } from "@app/customer-portal/services/customer-portal.service";
import { CustomerService } from "@app/customer/services/customer.service";
import { CustomerDbService } from "@app/customer/store/service/customer-db.service";
import { ClareSystemHelper } from "@app/project/model/clare-system";
import { DataActions } from "@app/project/store/actions";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { SendEmailDatabaseService } from "@app/shared/databases/email.database";
import { PushServerDatabaseService } from "@app/shared/databases/push-server.database";
import { ActionWord } from "@app/shared/resources/action-word";
import { toAction } from "@app/shared/resources/data-actions";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { EquatorProjectinfoService } from "@app/shared/services/equator-api/equator-projectinfo/equator-projectinfo.service";
import { User } from "@app/user/model/user";
import { Action } from "@ngrx/store";
import { CustomerLocationActions } from "../actions";

const CustomerLocationActionTypes = CustomerLocationActions.ActionTypes;

@Injectable()
export class CustomerLocationEffects {
    getLocationsByCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.GetLocationsByCustomer),
            map((action: CustomerLocationActions.GetLocationsByCustomer) => action.payload),
            switchMap(payload =>
                toAction()(
                    this.customerPortalDbService.getLocationsByCustomer(payload.customerId).pipe(
                        map(result => {
                            const claresystems = (result ?? []).map((claresystem: any) =>
                                new ClareSystemHelper().fromDbObject({ ...claresystem, primaryContact: claresystem.userinfo1 })
                            );

                            return {
                                claresystems,
                                customerId: payload.customerId,
                            };
                        })
                    ),
                    CustomerLocationActions.GetLocationsByCustomerSuccess,
                    CustomerLocationActions.GetLocationsByCustomerFailure
                )
            ),
            catchError(e => of(e))
        )
    );

    inviteUserToLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.InviteUserToLocation),
            map((action: CustomerLocationActions.InviteUserToLocation) => action.payload),
            exhaustMap(payload => {
                payload.invitedUser.inviteCode = this.customerService.generateCustomerInviteCode();
                return this.customerPortalDbService.getUserByEmail(payload.invitedUser, payload.uuid).pipe(
                    exhaustMap((result: { invitedUser: User }) => {
                        if (!result?.invitedUser) {
                            return of(
                                new CustomerLocationActions.InviteUserToLocationFailure(
                                    new DataServiceError("There was an error generating an invite for the user", payload)
                                )
                            );
                        }

                        result.invitedUser = {
                            ...payload.invitedUser,
                            ...result.invitedUser,
                        };

                        const userResult = Object.assign(payload, result);

                        let sendUserEmailAction: Action;
                        const actionPayload = {
                            primaryUser: userResult.primaryUser,
                            invitedUser: userResult.invitedUser,
                            claresystem: userResult.claresystem,
                        };

                        if (!userResult.invitedUser.gitkitId) {
                            sendUserEmailAction = new SendNewInvitedUserEmail(actionPayload);
                        } else {
                            sendUserEmailAction = new SendExistingInvitedUserEmail(actionPayload);
                        }

                        this.customerPortalService.clearLocationUsers(userResult.claresystem);
                        return toAction(sendUserEmailAction)(
                            of(actionPayload),
                            CustomerLocationActions.InviteUserToLocationSuccess,
                            CustomerLocationActions.InviteUserToLocationFailure
                        ).pipe(
                            catchError(e => {
                                let err = e;
                                console.error(e);
                                if (e && e.code === "23505") {
                                    err = new DataServiceError(
                                        `This email address is already connected to this location.
                                                Verify the user has signed in with the correct email address.`,
                                        payload
                                    );
                                }
                                return of(new CustomerLocationActions.InviteUserToLocationFailure(err));
                            })
                        );
                    }),
                    catchError(DataActions.toError(CustomerLocationActions.InviteUserToLocationFailure))
                );
            }),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        )
    );

    inviteUserToLocationSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.InviteUserToLocationSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.saved, null, "location user"))
        )
    );

    inviteUserToLocationFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.InviteUserToLocationFailure),
            map(DataActions.handleErrorMessage(ActionWord.saved, "location user"))
        )
    );

    updateUserInviteCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.UpdateCustomerInviteCode),
            exhaustMap((action: CustomerLocationActions.UpdateCustomerInviteCode) =>
                toAction()(
                    this.customerDbService.updateCustomer(action.payload.invitedUser).pipe(
                        map(({ customer }) => ({
                            ...action.payload,
                            invitedUser: customer,
                        }))
                    ),
                    CustomerLocationActions.SendExistingInvitedUserEmail,
                    CustomerLocationActions.SendExistingInvitedUserEmailFailure
                )
            ),
            catchError(DataActions.toError(CustomerLocationActions.SendExistingInvitedUserEmailFailure))
        )
    );

    sendExistingUserInviteEmail$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.SendExistingInvitedUserEmail),
            map((action: CustomerLocationActions.SendExistingInvitedUserEmail) => action.payload),
            exhaustMap(payload =>
                DataActions.toAction()(
                    this.emailDbService.sendEmailRequest$(
                        payload.invitedUser.email,
                        payload.invitedUser.firstName + " " + payload.invitedUser.lastName,
                        `Welcome to the new ClareHome system`,
                        // eslint-disable-next-line max-len
                        `<p>Welcome to the ClareHome system. The Primary Customer of a ClareHome system has invited you to connect your ClareHome mobile app to the new system. To get started, just sign into the ClareHome app on your mobile device and connect to your new location. Note: If this is not the email address you currently use with your ClareHome app, the Primary Customer of the new location will need to re-invite you at the correct email address.</p>
        <p>Thanks, we’re here to help,<br />The Clare Team</p>`
                    ),
                    CustomerLocationActions.SendNewInvitedUserEmailSuccess,
                    CustomerLocationActions.SendNewInvitedUserEmailFailure
                )
            ),
            catchError(DataActions.toError(CustomerLocationActions.SendNewInvitedUserEmailFailure))
        )
    );
    SendUserInviteEmailSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.SendNewInvitedUserEmailSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.sent, null, "Email to invite customer"))
        )
    );

    sendNewUserInviteEmail$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.SendNewInvitedUserEmail),
            map((action: CustomerLocationActions.SendNewInvitedUserEmail) => action.payload),
            exhaustMap(payload =>
                DataActions.toAction()(
                    this.emailDbService.sendEmailRequest$(
                        payload.invitedUser.email,
                        payload.invitedUser.firstName + " " + payload.invitedUser.lastName,
                        `Get started with ClareHome`,
                        // eslint-disable-next-line max-len
                        `<p>Welcome to the ClareHome system. The Primary Customer of a Clare system has invited you to connect your ClareHome mobile app to the new system. To get started, click the link below to visit the ClareHome sign-up page. Sign up using your email address (the one this email was sent to) or using one of the social sign in options that is tied to your email.</p>
    <p><a href="${window.location.protocol}//${window.location.host}/signup?staff=false&email=${encodeURIComponent(
                            payload.invitedUser.email
                        )}&firstName=${encodeURIComponent(payload.invitedUser.firstName)}&lastName=${encodeURIComponent(
                            payload.invitedUser.lastName
                        )}&inviteCode=${
                            payload.invitedUser.inviteCode
                            // eslint-disable-next-line max-len
                        }">ClareHome Signup</a></p><p>This is a unique sign-up page for you. After signing up, you’ll be given links for downloading the ClareHome app.</p><p>Thanks, we’re here to help,<br />The Clare Team</p>`
                    ),
                    CustomerLocationActions.SendNewInvitedUserEmailSuccess,
                    CustomerLocationActions.SendNewInvitedUserEmailFailure
                )
            ),
            catchError(DataActions.toError(CustomerLocationActions.SendNewInvitedUserEmailFailure))
        )
    );

    getCustomerRoleId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActions.ActionTypes.GetCustomerRoleId),
            exhaustMap(_ => this.customerPortalDbService.getCustomerRoleId()),
            map(customerRoleId => new CustomerLocationActions.GetCustomerRoleIdSuccess({ customerRoleId }))
        )
    );

    getLocationUsers$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.GetLocationUsers),
            map((action: CustomerLocationActions.GetLocationUsers) => action.payload),
            switchMap(payload => {
                const loadData$ = payload?.claresystem?.id
                    ? this.customerPortalDbService
                          .getLocationUsers(payload.claresystem.id)
                          .pipe(map(users => ({ users, claresystem: payload.claresystem })))
                    : // [DDB-3001] if the claresystem id is falsy, the users should be set back to the empty array
                      // without calling API
                      of({ users: [], claresystem: payload.claresystem });
                return toAction()(
                    loadData$,
                    CustomerLocationActions.GetLocationUsersSuccess,
                    CustomerLocationActions.GetLocationUsersFailure
                );
            })
        )
    );

    deleteLocationUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.DeleteLocationUser),
            switchMap((action: CustomerLocationActions.DeleteLocationUser) =>
                toAction()(
                    this.customerPortalDbService.removeUserFromLocation(action.payload.userInfoId, action.payload.claresystem.id).pipe(
                        tap(_ => {
                            this.pushServerDBService.unregisterUser$(action.payload.user?.uid, action.payload.claresystem.uuid);
                        }),
                        map(_ => action.payload)
                    ),
                    CustomerLocationActions.DeleteLocationUserSuccess,
                    CustomerLocationActions.DeleteLocationUserFailure
                )
            ),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        )
    );

    removeLocation$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.RemoveLocation),
            switchMap((action: CustomerLocationActions.RemoveLocation) =>
                toAction()(
                    this.customerPortalDbService.removeUserFromLocation(action.payload.userInfoId, action.payload.claresystem.id).pipe(
                        tap(_ => {
                            this.pushServerDBService.unregisterUser$(action.payload.user.gitkitId, action.payload.claresystem.uuid);
                        }),
                        map(_ => action.payload)
                    ),
                    CustomerLocationActions.RemoveLocationSuccess,
                    CustomerLocationActions.DeleteLocationUserFailure
                )
            ),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        )
    );

    deleteLocationUserSuccess$: Observable<Action> = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CustomerLocationActionTypes.DeleteLocationUserSuccess),
                tap(_ => {
                    this.postMessage();
                })
            ),
        { dispatch: false }
    );

    removeLocationSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.RemoveLocationSuccess),
            tap(_ => {
                if ((window as any).mchObserver) {
                    (window as any).mchObserver.postMessage("refreshLocations");
                }
            }),
            map(DataActions.handleSuccessMessage2(ActionWord.removed, null, "user location"))
        )
    );

    updateLocation$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.UpdateLocation),
            switchMap((action: CustomerLocationActions.UpdateLocation) =>
                toAction()(
                    this.customerPortalDbService.updateLocation(action.payload.claresystem).pipe(map(_ => action.payload)),
                    CustomerLocationActions.UpdateLocationSuccess,
                    CustomerLocationActions.UpdateLocationFailure
                )
            ),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        )
    );

    updateLocationSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.UpdateLocationSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.saved, null, "location")),
            tap(_ => {
                this.postMessage();
            })
        )
    );

    private postMessage() {
        if ((window as any).mchObserver) {
            (window as any).mchObserver.postMessage("refreshLocations");
            (window as any).mchObserver.postMessage("closeWebView");
        }
    }

    deleteLocationUserConfirmation$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.DeleteLocationUserConfirmation),
            exhaustMap((action: CustomerLocationActions.DeleteLocationUserConfirmation) =>
                this.deleteConfirmationService.open(
                    action.payload,
                    CustomerLocationActions.DeleteLocationUserConfirmationDismiss,
                    CustomerLocationActions.DeleteLocationUser,
                    action.payload.user.email,
                    "Remove",
                    " from this location"
                )
            )
        )
    );

    removeLocationConfirmation$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.RemoveLocationConfirmation),
            exhaustMap((action: CustomerLocationActions.RemoveLocationConfirmation) =>
                this.deleteConfirmationService.open(
                    action.payload,
                    CustomerLocationActions.DeleteLocationUserConfirmationDismiss,
                    CustomerLocationActions.RemoveLocation,
                    action.payload.user.email,
                    "Remove",
                    " from this location"
                )
            )
        )
    );

    getLocationData$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.GetLocationData),
            exhaustMap((action: CustomerLocationActions.GetLocationData) =>
                toAction()(
                    this.equatorService.getConfiguration(action.payload.uuid).pipe(map(result => ({ result, uuid: action.payload.uuid }))),
                    CustomerLocationActions.GetLocationDataSuccess,
                    CustomerLocationActions.GetLocationDataFailure
                )
            )
        )
    );

    /*remove all mobile users*/
    removeAllMobileUsersConfirmation$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.RemoveAllMobileUsersConfirmation),
            exhaustMap((action: CustomerLocationActions.RemoveAllMobileUsersConfirmation) =>
                this.deleteConfirmationService.open(
                    action.payload,
                    CustomerLocationActions.DeleteLocationUserConfirmationDismiss,
                    CustomerLocationActions.RemoveAllMobileUsersProcess,
                    " all mobile users",
                    "Remove"
                )
            )
        )
    );

    deleteLocationUserConfirmationDismiss$: Observable<Action> = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CustomerLocationActionTypes.DeleteLocationUserConfirmationDismiss),
                tap((action: CustomerLocationActions.DeleteLocationUserConfirmationDismiss) => {
                    action.payload.onDone?.emit(false);
                })
            ),
        { dispatch: false }
    );

    removeAllMobileUsers$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.RemoveAllMobileUsersProcess),
            switchMap((action: CustomerLocationActions.RemoveAllMobileUsersProcess) =>
                toAction()(
                    this.customerPortalDbService.removeAllMobileUser(action.payload.claresystem.id).pipe(
                        tap(_ => {
                            this.pushServerDBService.unregisterUsers$(action.payload.claresystem.uuid);
                        }),
                        map(_ => action.payload)
                    ),
                    CustomerLocationActions.RemoveAllMobileUsersSuccess,
                    CustomerLocationActions.DeleteLocationUserFailure
                )
            ),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        )
    );

    removeAllMobileUsersSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerLocationActionTypes.RemoveAllMobileUsersSuccess),
            mergeMap((action: CustomerLocationActions.RemoveAllMobileUsersSuccess) => {
                action.payload.onDone?.emit(true);
                const actions: Action[] = [DataActions.handleSuccessMessage(ActionWord.removed, null, "mobile app users")(action)];
                if (action.payload.claresystem) {
                    actions.push(new ClearLocationUsersState({ claresystem: action.payload.claresystem }));
                }
                return actions;
            })
        )
    );
    /*remove all mobile users*/

    constructor(
        private actions$: Actions,
        private readonly customerPortalDbService: CustomerPortalDbService,
        private readonly emailDbService: SendEmailDatabaseService,
        private deleteConfirmationService: DeleteConfirmationService,
        private readonly customerDbService: CustomerDbService,
        private readonly customerService: CustomerService,
        private readonly equatorService: EquatorProjectinfoService,
        private readonly pushServerDBService: PushServerDatabaseService,
        private readonly customerPortalService: CustomerPortalService
    ) {}
}
